import classNames from 'classnames/bind';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { ChevronDown } from '../../assets/icons/Icons';
import Avatar from '../../components/Avatar/Avatar';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import { isBrowserHandledLinkClick } from '../../utils/linkClick';
import Styles from './TopHat.module.scss';

const cx = classNames.bind(Styles);

interface TopHatNavItem {
  id: string;
  title: string;
  href: string;
}

interface TopHatCurrentNav {
  title?: string;
  activeNav?: string;
  nav?: TopHatNavItem[];
  breadcrumbs?: TopHatNavItem[];
  tabs?: TopHatNavItem[];
  activeTab?: string;
}

interface ITopHatContext {
  hasBeenInit: boolean;
  title: string;
  nav: TopHatNavItem[];
  breadcrumbs: TopHatNavItem[];
  tabs: TopHatNavItem[];
  activeTab: string;
  activeNav: string;

  setTitle: (title: string) => void;
  setNav: (nav: TopHatNavItem[]) => void;
  setBreadcrumbs: (breadcrumbs: TopHatNavItem[]) => void;
  setTabs: (tabs: TopHatNavItem[]) => void;
  setActiveTab: (activeTab: string) => void;
  setActiveNav: (activeNav: string) => void;
  setCurrentNav: (settings: TopHatCurrentNav) => void;
  setHasBeenInit: (hasBeenInit: boolean) => void;

  navigate: ((path: string) => void) | null;
}

const TopHatCtx = createContext<ITopHatContext>({
  hasBeenInit: false,
  title: 'Title Not Set',
  nav: [],
  breadcrumbs: [],
  tabs: [],
  activeTab: 'nil',
  activeNav: 'nil',
  setTitle: () => {},
  setNav: () => {},
  setBreadcrumbs: () => {},
  setTabs: () => {},
  setActiveNav: () => {},
  setActiveTab: () => {},
  setCurrentNav: () => {
    console.warn('TopHat Not Intialized, you are probably outside the context window.');
  },
  setHasBeenInit: () => {},
  navigate: null,
});

interface TopHatContextProps {
  children: React.ReactNode | React.ReactNode[] | null;
  navigate?: NavigateFunction | ((path: string) => void | null);
  initialValue: TopHatCurrentNav;
}

function navItemEquel(a: TopHatNavItem[] | null, b: TopHatNavItem[] | null) {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; ++i) {
    if (a[i].id !== b[i].id || a[i].href !== b[i].href) {
      return false;
    }
  }
  return true;
}

function isOverflown(element) {
  return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
}

export function TopHatContext({ children, navigate, initialValue }: TopHatContextProps) {
  const [lastInitVal, setLastInitVal] = useState<TopHatCurrentNav | null>(null);
  const [hasBeenInit, setHasBeenInit] = useState<boolean>(false);
  const [title, setTitle] = useState('Title Not Set');
  const [nav, setNav] = useState<TopHatNavItem[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<TopHatNavItem[]>([]);
  const [tabs, setTabs] = useState<TopHatNavItem[]>([]);
  const [activeNav, setActiveNav] = useState<string>('nil');
  const [activeTab, setActiveTab] = useState<string>('nil');

  const setCurrentNav = ({
    title: titleIn,
    activeNav: anIn,
    nav: navIn,
    breadcrumbs: bcIn,
    tabs: tabsIn,
    activeTab: atIn,
  }: TopHatCurrentNav) => {
    if (titleIn) {
      setTitle(titleIn);
    }
    if (anIn) {
      setActiveNav(anIn);
    }
    if (atIn) {
      setActiveTab(atIn);
    }

    // Arrays so check if unique by id
    if (navIn && !navItemEquel(nav, navIn)) {
      setNav(navIn);
    }
    if (bcIn && !navItemEquel(breadcrumbs, bcIn)) {
      setBreadcrumbs(bcIn);
    }
    if (tabsIn && !navItemEquel(tabs, tabsIn)) {
      setTabs(tabsIn);
    }
  };

  useEffect(() => {
    if (initialValue) {
      if (JSON.stringify(lastInitVal) !== JSON.stringify(initialValue)) {
        setHasBeenInit(true);
        setLastInitVal(initialValue);
        setCurrentNav(initialValue);
      }
    }
  }, [initialValue]);

  return (
    <TopHatCtx.Provider
      value={{
        hasBeenInit,
        title,
        nav,
        breadcrumbs,
        tabs,
        activeTab,
        activeNav,
        setTitle,
        setNav,
        setBreadcrumbs,
        setTabs,
        setActiveTab,
        setActiveNav,
        setCurrentNav,
        setHasBeenInit,
        navigate: navigate || null,
      }}>
      {children}
    </TopHatCtx.Provider>
  );
}

export function useTopHat(settings?: TopHatCurrentNav) {
  const tctx = useContext(TopHatCtx);

  useEffect(() => {
    if (settings) {
      tctx.setHasBeenInit(true);
      tctx.setCurrentNav(settings);
    }
  }, []);

  return {
    setTopHat: tctx.setCurrentNav,
  };
}

interface TopHatNavProps {
  items: TopHatNavItem[] | null;
  selected: string;
  navigate: ((n: string) => void) | null;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

function TopHatNav({ items, selected, navigate, onClick }: TopHatNavProps) {
  if (!items || items.length === 0) {
    return <></>;
  }

  return (
    <ul>
      {items.map((item) => (
        <li key={item.id}>
          <a
            href={item.href}
            onClick={(e) => {
              if (navigate && !isBrowserHandledLinkClick(e)) {
                e.preventDefault();
                navigate(item.href);
              }
              if (onClick) {
                onClick(e);
              }
            }}
            className={selected === item.id ? Styles['active'] : ''}>
            {item.title}
          </a>
        </li>
      ))}
    </ul>
  );
}

interface ITopHatProps extends React.HTMLAttributes<HTMLDivElement> {}

export function TopHat({ className, ...props }: ITopHatProps) {
  const ctx = useContext(TopHatCtx);
  const topNavInner = useRef<HTMLDivElement>(null);
  const navPopover = useRef<HTMLDivElement>(null);
  const [isOf, setIsOf] = useState(false);

  const crumbs = ctx.breadcrumbs.map((item) => ({
    label: item.title,
    path: item.href,
    useNavigate: ctx.navigate,
  }));

  const resizeHandler = () => {
    // Check if the nav is overflowing and do something
    if (topNavInner.current) {
      const of = isOverflown(topNavInner.current);
      if (isOf !== of && of !== false) {
        setIsOf(of);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    resizeHandler(); // Should catch initial render after the ref is set
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [topNavInner.current]);

  const navPopoverClick = () => {
    if (navPopover.current && navPopover.current.hidePopover) {
      navPopover.current.hidePopover();
      navPopover.current.style.display = 'none'; //safari mobile 16 fix
    } else if (navPopover.current) {
      navPopover.current.style.display = 'none'; //safari mobile 16 fix
    }
  };

  if (!ctx.hasBeenInit) {
    return <></>; // This is to prevent the initial animations from playing
  }

  const activeTopNav = ctx.nav.find((item) => item.id === ctx.activeNav) || { title: 'Unknown', id: '', href: '' };

  return (
    <div className={Styles['tophat'] + ` ${className}`} {...props}>
      <div className={Styles['top-outer']}>
        <div className={Styles['top']}>
          <div className={cx('title', { nonav: !ctx.nav || ctx.nav.length === 0 })}>
            <h1>{ctx.title}</h1>
          </div>
          <div className={cx('topnav', { nonav: !ctx.nav || ctx.nav.length === 0 })}>
            <div className={Styles['topnav-inner']} ref={topNavInner}>
              {!isOf && <TopHatNav items={ctx.nav} selected={ctx.activeNav} navigate={ctx.navigate} />}
              <div className={cx('mobile', { 'show-desktop': isOf })}>
                <button
                  popoverTarget={Styles['tophat-topnav']}
                  onClick={() => {
                    if (navPopover?.current) {
                      navPopover.current.style.display =
                        navPopover.current.style.display === 'block' ? 'none' : 'block'; //safari mobile 16 fix
                    }
                  }}>
                  {activeTopNav.title} {ChevronDown}{' '}
                </button>
                <div popover={'auto'} id={Styles['tophat-topnav']} ref={navPopover}>
                  <TopHatNav
                    items={ctx.nav}
                    selected={ctx.activeNav}
                    navigate={ctx.navigate}
                    onClick={navPopoverClick}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={Styles['avatar']}>
            <Avatar />
          </div>
        </div>
      </div>
      <div className={cx('brim', { empty: crumbs.length === 0 && ctx.tabs.length === 0 })}>
        <div
          className={cx('brim-inner', {
            'has-tabs': ctx.tabs.length > 0,
            empty: crumbs.length === 0 && ctx.tabs.length === 0,
          })}>
          <div className={cx('crumbs', { nobread: crumbs.length === 0 })}>
            <Breadcrumbs crumbs={crumbs} />
          </div>
          <div className={cx('tabs', { notab: ctx.tabs.length === 0 })}>
            <TopHatNav items={ctx.tabs} selected={ctx.activeTab} navigate={ctx.navigate} />
          </div>
        </div>
      </div>
    </div>
  );
}
